import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import { MiIcon } from "@miplatform/components";
import { PERMISSIONS } from "@miplatform/constants";
import { IBidOptionResponse } from "@miplatform/types";
import { mdiChevronDown } from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import { useCreateBidContext } from "../../../CreateBidContext";
import {
  OptionPriceDetails,
  OptionPriceSummary,
} from "../OptionPriceDetails/OptionPriceDetails";
import { useOptionPriceDetailsDrawerViewModel } from "./useOptionPriceDetailsDrawerViewModel";

export function OptionPriceDetailsDrawer({
  option,
}: OptionPriceDetailsDrawerProps) {
  const { classes, cx } = useStyles();
  const { form } = useCreateBidContext();
  const { getValues } = form;
  const { builderId } = getValues();
  const { getPriceDetails, priceDetails } =
    useOptionPriceDetailsDrawerViewModel();
  const { hasPermission } = usePermissions();
  const canReadPricing = hasPermission(PERMISSIONS.PRICING.READ);

  useEffect(() => {
    getPriceDetails(builderId, option);
  }, [option.planAreas]);

  if (!priceDetails) return null;

  return (
    <Accordion
      defaultExpanded
      disableGutters
      className={cx(classes.accordion, classes.drawsAccordion)}
    >
      <AccordionSummary
        expandIcon={canReadPricing && <MiIcon path={mdiChevronDown} />}
        className={cx(classes.sectionHeader, classes.drawsSectionHeader)}
      >
        <Typography>{option.name}</Typography>
        <Box className={classes.drawsWrapper}>
          <OptionPriceSummary priceDetails={priceDetails} />
        </Box>
      </AccordionSummary>
      {canReadPricing && (
        <AccordionDetails className={classes.drawsSectionBody}>
          <OptionPriceDetails priceDetails={priceDetails} />
        </AccordionDetails>
      )}
    </Accordion>
  );
}

const useStyles = makeStyles()((theme) => ({
  accordion: {
    width: "100%",
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: 4,
    backgroundColor: theme.palette.common.white,
    boxShadow: "none",
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: 0,
    },
  },
  drawsAccordion: {
    borderColor: theme.palette.grey.accent,
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 2),
    borderBottom: `1px solid ${theme.palette.grey.accent}`,
    backgroundColor: theme.palette.grey.light,
    minHeight: 48,
    "&.Mui-expanded": {
      minHeight: 48,
    },
    "& .MuiAccordionSummary-content": {
      display: "flex",
      alignItems: "center",
      margin: 0,
      "&.Mui-expanded": {
        margin: 0,
      },
    },
  },
  drawsSectionHeader: {
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.common.white}`,
    paddingRight: 16,
  },
  drawsSectionBody: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  drawsWrapper: {
    display: "flex",
    alignItems: "center",
    marginLeft: "auto",
  },
}));

interface OptionPriceDetailsDrawerProps {
  option: IBidOptionResponse;
}
